import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, Page } from '@/components/Page';
import { api } from '@/lib/api';

interface TokenListItem {
  id: string;
  name: string;
  scopes: string[];
  environment: 'live' | 'test';
  token_prefix: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface TokenCreatedResponse extends TokenListItem {
  token: string;
}

const ALL_SCOPES = [
  'files:read',
  'files:write',
  'files:delete',
  'buckets:read',
  'buckets:write',
  'transform:read',
  'transform:write',
  'webhooks:manage',
  'tokens:read',
  'usage:read',
];

export function TokensPage() {
  const qc = useQueryClient();
  const tokens = useQuery({
    queryKey: ['tokens'],
    queryFn: () => api<TokenListItem[]>('/v1/tokens'),
  });

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['files:read', 'files:write']);
  const [environment, setEnvironment] = useState<'live' | 'test'>('live');
  const [created, setCreated] = useState<TokenCreatedResponse | null>(null);

  const create = useMutation({
    mutationFn: (body: { name: string; scopes: string[]; environment: 'live' | 'test' }) =>
      api<TokenCreatedResponse>('/v1/tokens', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (data) => {
      setCreated(data);
      setName('');
      qc.invalidateQueries({ queryKey: ['tokens'] });
      toast.success(`Token "${data.name}" created`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to create token');
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/v1/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tokens'] });
      toast.success('Token revoked');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke token');
    },
  });

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!name || scopes.length === 0) {
      toast.error(!name ? 'Token name is required' : 'Select at least one scope');
      return;
    }
    await create.mutateAsync({ name, scopes, environment });
  }

  return (
    <Page title="API tokens" subtitle="Tokens never appear again after creation. Copy them now.">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">New token</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onCreate} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="token-name">Name</Label>
                <Input
                  id="token-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="production-uploader"
                />
              </div>
              <div className="flex gap-3 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="env"
                    checked={environment === 'live'}
                    onChange={() => setEnvironment('live')}
                  />
                  live
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="env"
                    checked={environment === 'test'}
                    onChange={() => setEnvironment('test')}
                  />
                  test
                </label>
              </div>
              <div className="space-y-1.5">
                <Label>Scopes</Label>
                <div className="max-h-48 space-y-1 overflow-auto rounded-md border p-2">
                  {ALL_SCOPES.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={scopes.includes(s)}
                        onChange={(e) =>
                          setScopes((cur) =>
                            e.currentTarget.checked ? [...cur, s] : cur.filter((x) => x !== s),
                          )
                        }
                      />
                      <span className="font-mono text-xs">{s}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button type="submit" disabled={create.isPending} className="w-full">
                {create.isPending ? 'Creating…' : 'Create token'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="md:col-span-2">
          {tokens.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : !tokens.data || tokens.data.length === 0 ? (
            <EmptyState title="No tokens yet" />
          ) : (
            <Card>
              <CardContent className="divide-y p-0">
                {tokens.data.map((t) => (
                  <div key={t.id} className="flex items-center justify-between px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{t.name}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-mono">{t.token_prefix}…</span>
                        <Badge variant="outline">{t.environment}</Badge>
                        <span>{t.scopes.length} scopes</span>
                      </div>
                    </div>
                    {t.revoked_at ? (
                      <Badge variant="outline">revoked</Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => revoke.mutate(t.id)}
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={!!created} onOpenChange={(open) => !open && setCreated(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save this token now</DialogTitle>
            <DialogDescription>
              You won't be able to see it again — copy it to a secure place.
            </DialogDescription>
          </DialogHeader>
          {created ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all rounded-md border bg-muted px-3 py-2 font-mono text-xs">
                  {created.token}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={async () => {
                    await navigator.clipboard.writeText(created.token);
                    toast.success('Token copied to clipboard');
                  }}
                  aria-label="Copy token"
                >
                  <Copy />
                </Button>
              </div>
              <pre className="overflow-auto rounded-md border bg-muted px-3 py-2 text-xs">
{`curl https://api.storeflare.io/v1/buckets \\
  -H "Authorization: Bearer ${created.token}"`}
              </pre>
            </div>
          ) : null}
          <DialogFooter>
            <Button onClick={() => setCreated(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
